import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Phone, 
  Mail, 
  MapPin,
  Linkedin, 
  Twitter, 
  Facebook,
  ArrowUp,
  Heart
} from "lucide-react";

const LOGO_URL = "/logo-white.png";

const Footer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const footerLinks = {
    services: [
      { name: "CRM Development", href: "#services" },
      { name: "HRMS Solutions", href: "#services" },
      { name: "DNC Scrubber", href: "#services" },
      { name: "Web Applications", href: "#services" },
      { name: "Workflow Automation", href: "#services" },
    ],
    company: [
      { name: "About Us", href: "#about" },
      { name: "Our Services", href: "#services" },
      { name: "Portfolio", href: "#portfolio" },
      { name: "Contact", href: "#contact" },
    ],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <footer ref={ref} className="relative bg-slate-950 pt-20 pb-8 overflow-hidden" data-testid="footer">
      {/* Animated top divider */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1 }}
      />

      {/* Background decoration */}
      <motion.div
        className="absolute top-1/2 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Brand column */}
          <motion.div variants={itemVariants} className="lg:col-span-1">
            <motion.img
              src={LOGO_URL}
              alt="Mighty Routes"
              className="h-10 mb-6"
              whileHover={{ scale: 1.05 }}
              data-testid="footer-logo"
            />
            <p className="text-slate-400 mb-6 leading-relaxed">
              Innovating Software for Smarter Call Centers. We build powerful, 
              scalable solutions that transform how businesses operate.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Linkedin, href: "https://linkedin.com", name: "linkedin" },
                { icon: Twitter, href: "https://twitter.com", name: "twitter" },
                { icon: Facebook, href: "https://facebook.com", name: "facebook" },
              ].map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-blue-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  data-testid={`social-${social.name}`}
                >
                  <social.icon size={18} className="text-slate-300 group-hover:text-white transition-colors" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Services column */}
          <motion.div variants={itemVariants}>
            <h4 className="text-white font-semibold mb-6 relative inline-block">
              Our Services
              <motion.span 
                className="absolute -bottom-1 left-0 h-0.5 bg-blue-500"
                initial={{ width: 0 }}
                animate={isInView ? { width: "100%" } : {}}
                transition={{ delay: 0.5, duration: 0.5 }}
              />
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <motion.li 
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.05 }}
                >
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href).scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-slate-400 hover:text-blue-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <motion.span
                      className="w-0 h-px bg-blue-400 group-hover:w-4 transition-all duration-300"
                    />
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Company column */}
          <motion.div variants={itemVariants}>
            <h4 className="text-white font-semibold mb-6 relative inline-block">
              Company
              <motion.span 
                className="absolute -bottom-1 left-0 h-0.5 bg-purple-500"
                initial={{ width: 0 }}
                animate={isInView ? { width: "100%" } : {}}
                transition={{ delay: 0.6, duration: 0.5 }}
              />
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <motion.li 
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.05 }}
                >
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href).scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-slate-400 hover:text-blue-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <motion.span
                      className="w-0 h-px bg-blue-400 group-hover:w-4 transition-all duration-300"
                    />
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact column */}
          <motion.div variants={itemVariants}>
            <h4 className="text-white font-semibold mb-6 relative inline-block">
              Contact Us
              <motion.span 
                className="absolute -bottom-1 left-0 h-0.5 bg-cyan-500"
                initial={{ width: 0 }}
                animate={isInView ? { width: "100%" } : {}}
                transition={{ delay: 0.7, duration: 0.5 }}
              />
            </h4>
            <div className="space-y-4">
              {[
                { icon: Phone, text: "+92 318 519 4055", href: "https://wa.me/923185194055", testId: "footer-whatsapp" },
                { icon: Mail, text: "mightyroutes@gmail.com", href: "mailto:mightyroutes@gmail.com", testId: "footer-email" },
              ].map((item, index) => (
                <motion.a
                  key={item.testId}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors group"
                  whileHover={{ x: 5 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  data-testid={item.testId}
                >
                  <motion.div
                    className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-colors"
                    whileHover={{ rotate: 10 }}
                  >
                    <item.icon size={16} />
                  </motion.div>
                  <span>{item.text}</span>
                </motion.a>
              ))}
              
              <motion.a
                href="https://www.google.com/maps/place/NUVEXA+LLC/@33.5979503,73.0204955,17z"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-slate-400 hover:text-blue-400 transition-colors group"
                whileHover={{ x: 5 }}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.7 }}
                data-testid="footer-address"
              >
                <motion.div
                  className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors"
                  whileHover={{ rotate: 10 }}
                >
                  <MapPin size={16} />
                </motion.div>
                <span className="text-sm">Office No. 1, 1st Floor, Ahmed Plaza, Range Rd, Afshan Colony, Rawalpindi, 46000</span>
              </motion.a>
            </div>

            {/* CTA */}
            <motion.a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact").scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 mt-6 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 btn-glow group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              data-testid="footer-cta"
            >
              Get Free Quote
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                →
              </motion.span>
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Bottom bar */}
        <motion.div 
          className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1 }}
        >
          <p className="text-slate-500 text-sm flex items-center gap-1">
            © {new Date().getFullYear()} Mighty Routes. Made with 
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <Heart size={14} className="text-red-500 fill-red-500" />
            </motion.span>
            All rights reserved.
          </p>
          
          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.1, y: -3 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 bg-slate-800 hover:bg-blue-500 rounded-full flex items-center justify-center transition-all duration-300 group"
            data-testid="scroll-to-top"
          >
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowUp size={18} className="text-white" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
